import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  LEAD_SOURCES, LEAD_STATUSES, LEAD_PRIORITIES, GENDERS,
} from "@/config/leads";

const EMPTY = {
  full_name: "", phone: "", whatsapp: "", email: "", gender: "", age: "",
  interest: "", source: "", campaign: "", medium: "", keyword: "", landing_page: "",
  status: "new", priority: "warm", owner_id: "", preferred_doctor: "",
  preferred_date: "", preferred_time: "", expected_revenue: "", notes: "",
};

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">{title}</p>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
  </div>
);

const Field = ({ label, required, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

export function LeadFormDialog({ open, onOpenChange, lead, owners = [], onSaved }) {
  const isEdit = Boolean(lead);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setErrors({});
      if (lead) {
        setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, lead[k] ?? ""])) });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, lead]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setInput = (k) => (e) => set(k)(e.target.value);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    else if (!/^[+\d][\d\s-]{6,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!form.interest.trim()) e.interest = "Interest / service is required";
    if (!form.source) e.source = "Source is required";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v === "" || v === null) {
        if (isEdit) payload[k] = null;
        return;
      }
      if (k === "age") payload[k] = parseInt(v, 10);
      else if (k === "expected_revenue") payload[k] = parseFloat(v);
      else payload[k] = v;
    });
    try {
      if (isEdit) await api.put(`/leads/${lead.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(isEdit ? "Lead updated" : "Lead created");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Lead" : "Add New Lead"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this lead's information." : "Capture a new enquiry into your workspace."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <Section title="Contact Information">
            <Field label="Full name" required>
              <Input value={form.full_name} onChange={setInput("full_name")} data-testid="lead-input-full_name" placeholder="e.g. Priya Nair" />
              {errors.full_name && <p className="text-xs text-red-600">{errors.full_name}</p>}
            </Field>
            <Field label="Phone number" required>
              <Input value={form.phone} onChange={setInput("phone")} data-testid="lead-input-phone" placeholder="+91 98xxx xxxxx" />
              {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
            </Field>
            <Field label="WhatsApp number">
              <Input value={form.whatsapp} onChange={setInput("whatsapp")} data-testid="lead-input-whatsapp" placeholder="Optional" />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={setInput("email")} data-testid="lead-input-email" placeholder="Optional" />
              {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
            </Field>
            <Field label="Gender">
              <Select value={form.gender || undefined} onValueChange={set("gender")}>
                <SelectTrigger data-testid="lead-select-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Age">
              <Input type="number" value={form.age} onChange={setInput("age")} data-testid="lead-input-age" placeholder="Optional" />
            </Field>
          </Section>

          <Section title="Service & Source">
            <Field label="Interest / service required" required className="sm:col-span-2">
              <Input value={form.interest} onChange={setInput("interest")} data-testid="lead-input-interest" placeholder="e.g. Dental Implants" />
              {errors.interest && <p className="text-xs text-red-600">{errors.interest}</p>}
            </Field>
            <Field label="Source" required>
              <Select value={form.source || undefined} onValueChange={set("source")}>
                <SelectTrigger data-testid="lead-select-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {errors.source && <p className="text-xs text-red-600">{errors.source}</p>}
            </Field>
            <Field label="Campaign">
              <Input value={form.campaign} onChange={setInput("campaign")} data-testid="lead-input-campaign" placeholder="Optional" />
            </Field>
            <Field label="Medium">
              <Input value={form.medium} onChange={setInput("medium")} data-testid="lead-input-medium" placeholder="Optional" />
            </Field>
            <Field label="Keyword">
              <Input value={form.keyword} onChange={setInput("keyword")} data-testid="lead-input-keyword" placeholder="Optional" />
            </Field>
            <Field label="Landing page" className="sm:col-span-2">
              <Input value={form.landing_page} onChange={setInput("landing_page")} data-testid="lead-input-landing_page" placeholder="Optional" />
            </Field>
          </Section>

          <Section title="Assignment & Status">
            <Field label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="lead-select-status"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger data-testid="lead-select-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Assigned owner">
              <Select value={form.owner_id || undefined} onValueChange={set("owner_id")}>
                <SelectTrigger data-testid="lead-select-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Expected revenue (₹)">
              <Input type="number" value={form.expected_revenue} onChange={setInput("expected_revenue")} data-testid="lead-input-revenue" placeholder="Optional" />
            </Field>
          </Section>

          <Section title="Preferred Appointment">
            <Field label="Preferred doctor">
              <Input value={form.preferred_doctor} onChange={setInput("preferred_doctor")} data-testid="lead-input-doctor" placeholder="Optional" />
            </Field>
            <Field label="Preferred date">
              <Input type="date" value={form.preferred_date} onChange={setInput("preferred_date")} data-testid="lead-input-date" />
            </Field>
            <Field label="Preferred time">
              <Input type="time" value={form.preferred_time} onChange={setInput("preferred_time")} data-testid="lead-input-time" />
            </Field>
          </Section>

          <div className="space-y-1.5">
            <Label className="text-slate-700">Notes</Label>
            <Textarea value={form.notes} onChange={setInput("notes")} data-testid="lead-input-notes" rows={3} placeholder="Any additional context about this lead…" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lead-form-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save changes" : "Create lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
